import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	inject,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild
} from "@angular/core";
import { Router } from "@angular/router";
import { ErrorClass, Fiat, FiatWallet, Offer, Trade } from "@models";
import { TRADE_TYPE, Warning } from "@models/trade-offer.model";
import { Helper } from "@helpers/functions";
import { SummaryComponent } from "../../../main/components/step-summary/summary/summary";
import { config, externalPaths } from "@config";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { UserService } from "@main-services/user.service";
import { FiatService } from "@main-services/currency/fiat.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { TradableService } from "@main-services/currency/tradable.service";
import { TradableWalletService } from "@main-services/wallet/tradable_wallet.service";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { Tradable } from "@models/interfaces/tradable";
import { BpModalHasSave } from "@models/interfaces/bp-modal-has-save";
import { BehaviorSubject, EMPTY, Observable } from "rxjs";
import { IndexTrade } from "@models/index_trade";
import { Metal } from "@models/asset/metal/metal";
import { StockToggleSlider } from "@models/stock-toggles";
import { Derivative } from "@models/derivative/currencies/derivative";
import { OFFER_MAX_REFRESHES, TOKEN_BEST_ID, TRADABLES_TYPE } from "@helpers/constants";
import { TradableTransaction } from "@models/transaction";
import { GoneError } from "bp-common-models-lib";
import { AssetInfoService } from "@main-services/split-data/asset-info.service";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { TranslationService } from "@core-services/translation.service";
import { InfoService } from "@core-services/info.service";
import { AcceptedOfferWallets, TradeOfferService } from "../../services/trade-offer.service";
import { ITBStep } from "@modules/shared/components/instant-trade-bonus/entitites/itb-step";
import { ITBTrade } from "@modules/shared/components/instant-trade-bonus/entitites/itb-trade";
import { LeveragedToken } from "@models/asset/cryptocoins/currencies/leveraged_tokens";
import { LeveragedTokenSlider } from "@modules/main/components/leveraged-tokens/leveraged-token-slider/leveraged-token-slider";
import { LeveragedTokensFacade } from "@modules/main/components/leveraged-tokens/state/leveraged-tokens.facade";
import { ILeverageTokenInfoSummaryViewModel } from "@modules/main/components/leveraged-tokens/leverage-token-info-summary/leverage-token-info-summary.interface";
import { map, take, tap } from "rxjs/operators";
import { ICashPlusFiatWallet } from "@modules/cashplus/entities/cashplus.model";
import { ITaxInOffer } from "@modules/taxes/domain/entities/taxes.model";
import { AssetGroupName } from "@modules/+new/shared/const/enums/asset-group-name.enum";
import { SecurityTokenToggleSlider } from "@models/security-token-toggles";
import { AgreementBundle, UserJourney } from "@modules/shared/terms-and-conditions/entities/terms-and-conditions.model";
import { TermsAndConditionsService } from "@modules/shared/terms-and-conditions/services/terms-and-conditions.service";
import {
	DYNAMIC_TERMS_TOKEN,
	TermsAndConditionsUpdatesComponent
} from "@modules/shared/terms-and-conditions/components/terms-and-conditions-updates.component";
import {
	VISION_BENEFIT_TOGGLE,
	VisionOptinToggleComponent
} from "@modules/web3/ui/vision-optin-toggle/vision-optin-toggle.component";
import {
	visionBenefitToggleTexts,
	VSNOptinToggleViewModel,
	WEB3_FEE_BENEFFIT_FLOW,
	Web3VSNFeeBenefitFF
} from "@modules/web3/interface/web3.benefit.ui.interface";
import { IVisionSummaryTotalComponentInput } from "@modules/web3/ui/vision-summary-total/vision-summary-total.component";
@Component({
	selector: "tradable-trade-summary",
	templateUrl: "./tradable-trade-summary.component.html",
	styleUrls: ["tradable-trade-summary.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class TradableTradeSummaryComponent extends SummaryComponent implements OnInit, OnDestroy, BpModalHasSave {
	protected termsAndConditionsService: TermsAndConditionsService = inject(TermsAndConditionsService);
	protected cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

	@ViewChild(DYNAMIC_TERMS_TOKEN) dynamic_terms: TermsAndConditionsUpdatesComponent;
	@ViewChild(VISION_BENEFIT_TOGGLE) visionBenefitToggle: VisionOptinToggleComponent;

	@Input() public data: Offer = null;
	@Input() public showAppTestWarning = false;

	//Taxes
	@Input() public taxWithheld: ITaxInOffer = null;

	/**
	 * Calling GQL only if:
	 * - trading Crypto
	 * - trading Metal
	 * - trading Derivatives (Stocks or ETFs) and feature flag is enabled
	 */
	@Input() public shouldCallGql: boolean = false;

	@Input()
	public itbData: ITBTrade = null;

	// cash plus selected asset
	@Input()
	public cashPlusAsset: ICashPlusFiatWallet;
	@Input()
	public isCashPlus = false;

	@Output()
	public onReceiveTradeId: EventEmitter<string> = new EventEmitter<string>();

	// Tax Withheld specific event emitter
	@Output()
	public onTaxWitheldUpdate: EventEmitter<ITaxInOffer | null> = new EventEmitter<ITaxInOffer | null>();

	public bundles: AgreementBundle[] = [];
	public oldTimer: number;
	public time: BehaviorSubject<number> = new BehaviorSubject<number>(0);
	public tac: boolean = false;
	public metal_terms: boolean = false;
	showGetOfferButton: boolean = true;

	termsLoading = true;

	public hodlOnly: boolean = false;
	public infoIntegration: boolean = false;
	public rebalanceTermsAccepted: boolean = false;
	public best2TermsAccepted: boolean = false;
	public stockRiskAccepted: boolean = false;
	public stockTermsToggles: StockToggleSlider = new StockToggleSlider();
	public leveragedTokenToggles: LeveragedTokenSlider = new LeveragedTokenSlider();
	public securityTokenToggles: SecurityTokenToggleSlider = new SecurityTokenToggleSlider();
	public disabled: boolean = false;
	public apiPending: boolean = false;
	public ITBStep = ITBStep;
	public leveragedTokenSuffix: string = "";
	public isShortBuy: boolean = false;
	private offerRefreshCounter: number = 0;
	public cfdFlag$: Observable<boolean> = this.launchDarklyService.isActive$(LD_FLAG.LEVERAGE_CFD);

	leverageTokenViewModel$: Observable<ILeverageTokenInfoSummaryViewModel> =
		this.leveragedTokensFacade.getLeverageTokenStateInfo$();
	public cfdEnabled: boolean = false;
	public isSecurityToken: boolean = false;
	public tradableType = TRADABLES_TYPE;
	public tradable: Tradable;

	// cashplus
	public cashplus_toggle: boolean = false;

	//fee transparency
	feeTransparencyEnabled$ = this.launchDarklyService.isActive$(LD_FLAG.FEE_TRANSPARENCY_CRYPTO_ONLY);
	bundles$: Observable<AgreementBundle[]> = EMPTY;

	//VSN benefit
	visionBenefitFF$ = this.launchDarklyService
		.getFeatureConfig$<Web3VSNFeeBenefitFF>(LD_FLAG.WEB3_VSN_FEE_BENEFIT)
		.pipe(map((d) => !!d[WEB3_FEE_BENEFFIT_FLOW.marketOrderSell]));

	visionSavingBenefitFF$ = this.launchDarklyService
		.getFeatureConfig$<Web3VSNFeeBenefitFF>(LD_FLAG.WEB3_VSN_FEE_BENEFIT)
		.pipe(map((d) => !!d[WEB3_FEE_BENEFFIT_FLOW.savingsPlan]));

	//vsnBenefitTotalVM: IVisionSummaryTotalComponentInput;
	vsnFeeBenefit?: VSNOptinToggleViewModel;
	vsnBenefitTotalVM: IVisionSummaryTotalComponentInput;
	private _visionBenefitToggleTexts = visionBenefitToggleTexts;
	private bonusFlag: boolean = null;

	public constructor(
		protected router: Router,
		protected apicallService: ApicallService,
		protected fiatwalletService: FiatWalletService,
		protected walletListService: TradableWalletListService,
		protected fiatService: FiatService,
		protected tradableListService: TradableListService,
		protected translationService: TranslationService,
		protected infoService: InfoService,
		protected masterdataService: MasterdataService,
		protected tradeOfferService: TradeOfferService,
		protected userService: UserService,
		protected assetInfoService: AssetInfoService,
		protected leveragedTokensFacade: LeveragedTokensFacade,
		protected launchDarklyService: LaunchDarklyService
	) {
		super();
	}

	public ngOnInit(): void {
		this.tradable = this.tradableListService.getItemById(this.data.getCurrencyId());
		this.isSecurityToken = this.tradable.getAssetGroupName() === AssetGroupName.SecurityToken;

		this.assetInfoService.fetchApiInfo(this.asset);
		if (!this.isIndexTrade()) {
			this.setOfferTimer();
		}

		if (this.isLeveragedToken()) {
			this.setLeveragedTokenSuffix();
			this.cfdFlag$.pipe(tap()).subscribe((flag) => {
				this.cfdEnabled = flag;
			});
			if (this.isBuy()) {
				this.leverageTokenViewModel$.pipe(tap()).subscribe((vm: ILeverageTokenInfoSummaryViewModel) => {
					this.isShortBuy = !vm.isLong;
				});
			}
		}

		let userJourney = this.data.getUserJourney();
		if (this.tradable.getCurrencyType() === TRADABLES_TYPE.INDEX) {
			userJourney = UserJourney.TradingCryptoIndexSell;
		}

		this.bundles$ = this.termsAndConditionsService.getAgreementBundleData$(userJourney);
		this.bundles$.pipe(take(1)).subscribe((bundles: AgreementBundle[]) => {
			this.bundles = bundles;
			if (bundles.length === 0) {
				this.termsLoading = false;
			}
		});

		//set VSN benefit data
		this.setVSNBenefitDataHandler();
	}

	private setVSNBenefitDataHandler(): void {
		//VSN fee benefit
		const vsnFeeCollection = this.data.getVsnFeeTokenCollection();

		//assign discount is exists
		if (vsnFeeCollection?.feeDiscount) {
			this._visionBenefitToggleTexts.heading.discount = +vsnFeeCollection.feeDiscount;
		}

		//assign texts
		this.vsnFeeBenefit = {
			vsnFeeCollection,
			texts: this._visionBenefitToggleTexts
		};

		//assign VSN fee benefit total viewmodel
		this.vsnBenefitTotalVM = {
			fiat: this.fiat,
			tradable: this.tradable,
			total: +this.data.getAmountFiat(),
			vsnFeeCollection: this.data.getVsnFeeTokenCollection()
		};
	}

	public get lang(): string {
		return this.translationService.getLanguage();
	}

	public isMetal(): boolean {
		return this.asset instanceof Metal;
	}

	public isDerivative(): boolean {
		return this.asset.isDerivative();
	}

	public isLeveragedToken(): boolean {
		return this.asset instanceof LeveragedToken;
	}

	public save(): void {
		this.acceptOffer();
	}

	protected acceptTermsAndBuy(userJourneys: UserJourney[] = [this.data.getUserJourney()]): void {
		if (this.bundles.length > 0) {
			this.termsAndConditionsService.acceptTerms(
				userJourneys,
				this.dynamic_terms?.termsAndConditionsForm?.value,
				() => {
					this.confirmPlanToAPI();
				}
			);
		} else {
			this.confirmPlanToAPI();
		}
	}

	acceptOffer(): void {
		if (this.isButtonDisabled) {
			return;
		}

		this.disabled = true;
		this.showGetOfferButton = false;

		if (this.dynamicTermsInvalid) {
			this.dynamic_terms?.wiggleIt();
			return;
		}
		this.acceptTermsAndBuy();
	}

	public setTermsLoadingFinished(): void {
		this.termsLoading = false;
		this.cdr.markForCheck();
	}

	public showDerivativeFooter(): boolean {
		return this.asset instanceof Derivative;
	}

	public getIsMandatePlan(): boolean {
		return false; // Base-implementation, is overwritten
	}

	public isBalanceLessThanAmount(): boolean {
		if (this.cashPlusAsset) {
			return +this.cashPlusAsset?.availableAssetBalance < +this.data.getAmountFiat();
		}

		return false;
	}

	public getSavingsConfirmSliderText(isHeader: boolean = false): string {
		if (isHeader) {
			return this.getIsMandatePlan()
				? "recurring_buy_modal_summary_confirm_header_sepa_mandate"
				: "recurring_buy_modal_summary_confirm_credit_card_my_name";
		}
		return this.getIsMandatePlan()
			? "recurring_buy_modal_summary_confirm_description_sepa_mandate"
			: "recurring_buy_modal_summary_confirm_credit_card_my_name_description";
	}

	public getSavingsConfirmSliderTooltip(): string {
		if (this.isSavings()) {
			return "recurring_buy_modal_summary_tooltip_credit_card";
		}
		return "fagg_tooltip";
	}

	public isHodlAsset(): boolean {
		return this.asset.isHodlTradable(this.data.getType());
	}

	public hasInfoIntegration(): boolean {
		return this.asset.getInfoIntegration().length > 0 && this.data.getType() === TRADE_TYPE.BUY;
	}

	public hasStockTerms(): boolean {
		return (this.isBuy() || this.isSell() || this.isSavings()) && this.isDerivative();
	}

	public hasLeveragedTokenTerms(): boolean {
		return (this.isBuy() || this.isSell()) && this.isLeveragedToken();
	}

	public hasSecurityTokenTerms(): boolean {
		return (this.isBuy() || this.isSell()) && this.isSecurityToken;
	}

	public showStockRiskToggle(): boolean {
		return (this.isBuy() || this.isSavings()) && this.userService.user.needsRiskToggle() && this.isDerivative();
	}

	public isStockRiskAccepted(): boolean {
		if (this.isBuy()) {
			return this.stockTermsToggles.investRiskAccepted();
		}
		return this.stockRiskAccepted;
	}

	public showBest2Terms(): boolean {
		return (
			this.masterdataService.isBest2ProductLive() &&
			(this.isBuy() || this.isSavings()) &&
			this.asset.getId() === TOKEN_BEST_ID
		);
	}

	public continue(): void {
		if (this.showDisabledRefresh()) {
			this.setRefreshInfoToaster();
			return;
		}

		if (!this.tac && !this.hasStockTerms()) {
			this.wiggle("tac");
		}

		if (this.hasStockTerms() && !this.stockTermsToggles.getAllEnabled()) {
			this.stockTermsToggles.wiggleAll();
		}

		if (this.hasSecurityTokenTerms() && !this.securityTokenToggles.getAllEnabled()) {
			this.securityTokenToggles.wiggleAll();
		}

		// cashplus toggle wiggle
		if (!this.cashplus_toggle) {
			this.wiggle("cashplus_toggle");
		}

		if (this.showStockRiskToggle() && !this.isStockRiskAccepted()) {
			if (this.isBuy()) {
				this.wiggle("investRisk");
			} else {
				this.wiggle("stockRiskAccepted");
			}
			return;
		}

		if (
			this.isLeveragedToken() &&
			(this.isBuy() || this.isSell()) &&
			!this.leveragedTokenToggles.getAllEnabled(this.showAppTestWarning, this.isBuy())
		) {
			this.leveragedTokenToggles.wiggleAll();
		}

		if (!this.hodlOnly && this.isHodlAsset()) {
			this.wiggle("hodlOnly");
			return;
		}
		if (!this.infoIntegration && this.hasInfoIntegration()) {
			this.wiggle("infoIntegration");
		}

		if (this.isIndexTrade() && this.isBuyOrSavings() && !this.rebalanceTermsAccepted) {
			this.wiggle("rebalanceTermsAccepted");
		}

		if (this.showBest2Terms() && !this.best2TermsAccepted) {
			this.wiggle("best2TermsAccepted");
		}

		if (!this.acceptedAll) {
			this.infoService.clearMessage();
			return;
		}

		this.confirmPlanToAPI();
	}

	private isBuyOrSavings(): boolean {
		return this.isBuy() || this.isSavings();
	}

	protected confirmPlanToAPI(): void {
		if (this.shouldCallGql) {
			this.acceptOfferGql();
			return;
		}

		this.acceptOfferRestApi();
	}

	private get walletService(): TradableWalletService<TradableWallet, TradableTransaction, Tradable> {
		return this.walletListService.serviceForId(this.data.getWalletId());
	}

	private get assetService(): TradableService<Tradable> {
		return this.tradableListService.serviceForId(this.data.getCurrencyId());
	}

	public get wallet(): TradableWallet {
		return this.walletService.getItemById(this.data.getWalletId());
	}

	public set wallet(w: TradableWallet) {
		this.walletService.upsert(w);
	}

	public get fiatwallet(): FiatWallet {
		return this.fiatwalletService.getItemById(this.data.getFiatWalletId());
	}

	public set fiatwallet(f: FiatWallet) {
		this.fiatwalletService.upsert(f);
	}

	public get asset(): Tradable {
		return this.assetService.getItemById(this.data.getCurrencyId());
	}

	public get fiatWallet(): FiatWallet {
		return this.fiatwalletService.getItemById(this.data.getFiatWalletId());
	}

	public get fiat(): Fiat {
		return this.fiatService.getItemById(this.fiatWallet.getFiatId());
	}

	public getFiatName(fiat: Fiat): string {
		if (!fiat) {
			return "";
		}
		return fiat.getName();
	}

	public getRecentPrice(): number {
		return this.data.bestFeeOptIn ? +this.data.getPriceBfc() : +this.data.getPrice();
	}

	public resetOffer(): void {
		if (this.oldTimer !== -1) {
			clearInterval(this.oldTimer);
		}
		this.tac = false;
		this.metal_terms = false;
		this.cashplus_toggle = false;
		this.increaseRefreshCounter();
		if (this.hasStockTerms()) {
			this.stockTermsToggles.disableSliders();
		}
		if (this.hasLeveragedTokenTerms()) {
			this.leveragedTokenToggles.disableSliders();
		}
		if (this.hasSecurityTokenTerms()) {
			this.securityTokenToggles.disableSliders();
		}

		//reset toggles for dynamic terms
		if (this.dynamic_terms) {
			this.dynamic_terms.resetToggles();
			if (this.showDisabledRefresh()) {
				this.dynamic_terms.disableToggles();
			}
		}
	}

	private setOfferTimer(): void {
		this.resetOffer();
		this.dynamic_terms?.enableToggles();
		this.time.next(this.data.getCountDown());
		clearInterval(this.oldTimer);
		const timer = window.setInterval(() => {
			this.time.next(this.time.getValue() - 1);
			if (this.time.getValue() <= 0) {
				clearInterval(timer);
				clearInterval(this.oldTimer);
				if (!this.hasExceededRefreshes()) {
					this.refresh();
				}
			}
		}, 1000);
		this.oldTimer = timer;
		if (this.data.bestFeeOptIn) {
			this.setAmountShort(this.data.getAmountAssetBfc(), this.asset);
		} else {
			this.setAmountShort(this.data.getAmountTradable(), this.asset);
		}
	}

	public userManualAction(clearToast: boolean = false): void {
		if (clearToast) {
			this.infoService.clearMessage();
		}
		this.resetRefreshCounter();
		this.refresh();
	}

	public refresh(): void {
		if (this.shouldCallGql) {
			this.getOfferFromGql();
			return;
		}

		this.getOfferFromRestApi();
	}

	public resetRefreshCounter(): void {
		this.offerRefreshCounter = 0;
		this.disabled = false;

		//enable VSN benefit toggle
		if (this.visionBenefitToggle) {
			this.visionBenefitToggle.toggleCtrl.enable();
		}
	}

	public increaseRefreshCounter(): void {
		this.offerRefreshCounter++;
		if (this.hasExceededRefreshes() && !this.disabled) {
			this.disabled = true;
			this.setRefreshInfoToaster();

			//disabled VSN benefit toggle
			if (this.visionBenefitToggle) {
				this.visionBenefitToggle.toggleCtrl.disable();
			}
		}
	}

	private setRefreshInfoToaster(): void {
		this.infoService.clearMessage();
		this.infoService.setInfo(
			this.translationService.translate("summary_new_offer_warning_description"),
			this.translationService.translate("summary_new_offer_warning_title"),
			0
		);
	}

	private hasExceededRefreshes(): boolean {
		return this.offerRefreshCounter > OFFER_MAX_REFRESHES;
	}

	public showDisabledRefresh(): boolean {
		return this.disabled;
	}

	public get acceptedAll(): boolean {
		return (
			(this.isSecurityToken && this.securityTokenToggles.getAllEnabled()) ||
			((!this.isLeveragedToken() ||
				(this.isLeveragedToken() &&
					this.leveragedTokenToggles.getAllEnabled(this.showAppTestWarning, this.isBuy()))) &&
				(this.hasStockTerms()
					? this.stockTermsToggles.getAllEnabled()
					: (this.isLeveragedToken() || this.tac) && !this.apiPending) &&
				(!this.showStockRiskToggle() || this.isStockRiskAccepted()) &&
				(this.infoIntegration || !this.hasInfoIntegration()) &&
				(this.hodlOnly || !this.isHodlAsset()) &&
				this.isIndexTermsAccepted() &&
				(!this.isMetal() ||
					(this.isMetal() &&
						((this.metal_terms && this.isBuyOrSavings()) ||
							(!this.metal_terms && this.isSell()) ||
							!this.metal_terms))) &&
				!this.showDisabledRefresh() &&
				(!this.showBest2Terms() || (this.showBest2Terms() && this.best2TermsAccepted)) &&
				(this.isCashPlusBalanceAndBuy() || this.isNotCashPlusBalanceAndBuy()))
		);
	}

	// is cash plus balance and buy helper
	private isNotCashPlusBalanceAndBuy(): boolean {
		return !this.isCashPlus || !this.isBalanceLessThanAmount() || !this.isBuy();
	}

	// is NOT cash plus balance and buy helper
	private isCashPlusBalanceAndBuy(): boolean {
		return this.isCashPlus && this.cashplus_toggle && this.isBuy() && this.isBalanceLessThanAmount();
	}

	public isIndexTermsAccepted(): boolean {
		if (!this.isIndexTrade()) {
			return true;
		}
		return this.isBuyOrSavings() === this.rebalanceTermsAccepted;
	}

	public get accepted(): boolean {
		return this.tac;
	}

	public getOfferFiatAmount(): number {
		return this.data.bestFeeOptIn ? +this.data.getAmountFiatBfc() : +this.data.getAmountFiat();
	}

	public set accepted(b: boolean) {
		this.tac = b;
	}

	public getHodlLink(): string {
		return this.translationService.getHdLink() + config.externalPaths["tradeOffer"]["hodlLink"];
	}

	public getInfoIntegrationLink(): string {
		return this.translationService.getHdLink() + config.externalPaths["info_integration"]["hdLink"];
	}

	public getMetalsLink(): string {
		const lang = TranslationService.getLanguageIsoCode();
		return (config.baseUrl + config.externalPaths["commodities"]["storageFeesLink"]).replace("%s", lang);
	}

	public setEdit(e: number): void {
		this.resetOffer();
		this.data.setDirectTradableWalletOffer(false);
		this.edit.emit(e);
		this.infoService.clearMessage();
	}

	public ngOnDestroy(): void {
		clearInterval(this.oldTimer);
		this.infoService.clearMessage();
	}

	public showTimer(): boolean {
		return !this.isSavings() && !this.isIndexTrade();
	}

	public isIndexTrade(): boolean {
		return this.data.isIndexTrade();
	}

	public isSavings(): boolean {
		return this.data.getType() === TRADE_TYPE.RECURRING_BUY;
	}

	public isBuy(): boolean {
		return this.data.getType() === TRADE_TYPE.BUY;
	}

	public isSell(): boolean {
		return this.data.getType() === TRADE_TYPE.SELL;
	}

	public getTitle(): string {
		if (this.isBuy()) {
			return this.asset.isToken() ? "fma_invest" : "buy_cryptocoin";
		}

		if (this.isSell()) {
			return this.asset.isToken() ? "fma_sell" : "sell_cryptocoin";
		}

		if (this.asset.isToken()) {
			return "recurring_buy_modal_title_buy_cryptocoins";
		}

		return "recurring_buy_modal_title_buy_cryptocoin";
	}

	public getSummaryTitle(n: number): string {
		if (this.asset.isToken() && n === 1) {
			return "fma_digital_asset";
		}

		return "trade_summary_step_" + n + "_" + (n > 0 ? this.data.getType() : "default");
	}

	public setLeveragedTokenSuffix(): void {
		const tradeType = this.data?.getType();
		const tradableBalance = this.walletListService.getWalletsForTradable(this.asset.getId())[0].getBalance();
		if (tradeType === TRADE_TYPE.BUY) {
			this.leveragedTokenSuffix = tradableBalance > 0 ? "increase" : "open";
		}
		if (tradeType === TRADE_TYPE.SELL) {
			this.leveragedTokenSuffix = +this.data?.getAmountTradable() === tradableBalance ? "close" : "decrease";
		}
	}

	public getIndexTermsLink(): string {
		return (config.baseUrl + config.externalPaths["index"]["prospectus"]).replace(
			"%s",
			TranslationService.getLanguageIsoCode()
		);
	}

	public getBest2TermsLink(): string {
		const language: string = TranslationService.getLanguageIsoCode() === "de" ? "de" : "en";
		return `${config.baseUrl}${language}${externalPaths.best.best_two_legal}`;
	}

	public showStockWarningMessage(): boolean {
		return this.data.showTradingHoursWarning();
	}

	public getStockWarningMessage(): Warning[] {
		return this.data.getStockWarnings();
	}

	// TODO: this can be removed when the GQL endpoint is used for all tradables
	private getOfferFromRestApi(): void {
		this.tradeOfferService.callTradeOffer(this.data).subscribe(
			(ret) => {
				this.newOfferReceived(ret[0]);
			},
			(error) => {
				this.handleOfferCreateErrors(error);
			}
		);
	}

	private getOfferFromGql(): void {
		this.data.setVisionBonus(this.bonusFlag);

		this.tradeOfferService.createOfferGQL(this.data).subscribe({
			next: (o) => this.newOfferReceived(o),
			error: (error) => this.handleOfferCreateErrors(error)
		});
	}

	private newOfferReceived(offer: Offer): void {
		this.data = this.tradeOfferService.resetOfferData(offer, this.data);

		// send TW data to parent component
		if (this.taxWithheld?.isTaxable) {
			this.onTaxWitheldUpdate.emit(this.data.getTax());
		}

		//update VSN benefit data
		this.setVSNBenefitDataHandler();

		this.setOfferTimer();
	}

	private acceptOfferRestApi(): void {
		// TODO: eslint fix
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		let acceptCall: Observable<any>;
		if (this.isIndexTrade()) {
			const data = new IndexTrade(this.data);
			const apiOption = new ApicallOption(IndexTrade, data);
			acceptCall = this.apicallService.post("indexes/" + this.data.getCurrencyId() + "/trades", apiOption);
		} else {
			const apiOption = new ApicallOption(Trade);
			acceptCall = this.apicallService.get("offer/" + this.data.getId() + "/accept", apiOption);
		}

		this.apiPending = true;

		acceptCall.subscribe(
			(res: Trade | IndexTrade) => {
				if (!this.isIndexTrade()) {
					this.resetOffer();
				}
				clearInterval(this.oldTimer);

				if (res[0]?.getId()) {
					this.onReceiveTradeId.emit(res[0].getId());
				}

				this.apicallService
					.get("wallet/" + this.wallet.getId(), new ApicallOption(this.walletService.WalletType))
					.subscribe((a) => (this.wallet = a[0]));
				const fCall = new ApicallOption(FiatWallet);
				this.apicallService
					.get("fiatwallet/" + this.fiatwallet.getId(), fCall)
					.subscribe((a) => (this.fiatwallet = a[0]));
				this.apiPending = false;
				this.finish.emit("SUCCESS");
			},
			(error) => {
				this.handleOfferAcceptErrors(error);
			}
		);
	}

	private acceptOfferGql(): void {
		this.apiPending = true;
		this.getTradeGqlEndpoint().subscribe(
			(response) => {
				this.handleOfferAcceptedGql(response);
			},
			(error) => {
				this.handleOfferAcceptErrors(error);
			},
			() => {
				this.apiPending = false;
				this.disabled = false;
			}
		);
	}

	private getTradeGqlEndpoint(): Observable<AcceptedOfferWallets | null> {
		const offerId = this.data.getId();
		const assetId = this.data.getCurrencyId();
		const fiatId = this.data.getFiatId();
		const fiatAmount = this.data.getAmountFiat();
		const fiatWalletId = this.data.getFiatWalletId();
		const tradeType = this.data.getType();
		const assetWalletId = this.wallet.isNew() || this.wallet.isDefault() ? null : this.data.getWalletId();
		const tradableWallet = this.walletService.WalletType;
		const sellAll = this.data.getSellAll();

		return this.isIndexTrade()
			? this.tradeOfferService.tradeBci(assetId, fiatId, fiatWalletId, fiatAmount, tradeType, sellAll)
			: this.tradeOfferService.acceptOffer(offerId, assetWalletId, tradableWallet);
	}

	private handleOfferAcceptedGql(response: AcceptedOfferWallets | null): void {
		if (!this.isIndexTrade()) {
			this.resetOffer();
		}
		clearInterval(this.oldTimer);

		if (!response) {
			this.finish.emit("PENDING");
			return;
		}
		const { tradeId } = response;
		this.updateWalletsAfterOfferAccepted(response);
		this.onReceiveTradeId.emit(tradeId);
		this.finish.emit("SUCCESS");
	}

	private updateWalletsAfterOfferAccepted(wallets: AcceptedOfferWallets): void {
		const isNewWallet = this.wallet.isNew();
		const { fiatWallet, assetWallet } = wallets;

		this.wallet = assetWallet;
		this.fiatwallet = fiatWallet;

		// Delete dummy wallet because the real one has been created and returned from the DB
		if (isNewWallet) {
			this.deleteDummyWallet();
			// Reference to this.data is used outside this component (TradeSuccessComponent)
			// We update wallet id to reflect the newly created wallet id from the DB
			this.data.setWalletId(assetWallet.getId());
		}
	}

	private deleteDummyWallet(): void {
		const dummyWallet = this.walletService.getItemById(this.data.getWalletId());
		this.walletService.deleteItem(dummyWallet);
	}

	private handleOfferCreateErrors(error): void {
		Helper.handleError(error);
		this.onClose.emit(true);
	}

	private handleOfferAcceptErrors(error): void {
		if (!this.isIndexTrade()) {
			this.refresh();
		}
		if (error instanceof ErrorClass) {
			if (error instanceof GoneError) {
				for (const e of error.responseErrors) {
					this.infoService.setError(e.title);
				}
			} else {
				this.infoService.setError(error);
				this.apiPending = false;
			}
		} else {
			console.error(error);
		}
	}

	public get dynamicTermsInvalid(): boolean {
		return !!this.dynamic_terms?.termsAndConditionsForm?.invalid || this.termsLoading;
	}

	public get isButtonDisabled(): boolean {
		return this.dynamicTermsInvalid || this.showDisabledRefresh();
	}

	onVSNToggle(value: boolean): void {
		this.bonusFlag = value;
		this.refresh();
	}
}
